'use client'

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import type {
  OperatorGroup,
  CreateOperatorGroupDTO,
  UpdateOperatorGroupDTO,
} from '@/lib/pbx/flow/types'

export function useOperatorGroups() {
  const [groups, setGroups] = useState<OperatorGroup[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const loadGroups = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth('/api/pbx/operator-groups')
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to fetch groups')
      }
      const data = await res.json()
      setGroups(data.groups || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated])

  useEffect(() => {
    loadGroups()
  }, [loadGroups])

  return {
    groups,
    isLoading,
    error,
    refetch: loadGroups,
  }
}

export function useOperatorGroupMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth } = useAuth()

  const createGroup = useCallback(async (dto: CreateOperatorGroupDTO) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth('/api/pbx/operator-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to create group')
      }

      const data = await res.json()
      return data.group as OperatorGroup
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const updateGroup = useCallback(async (id: string, dto: UpdateOperatorGroupDTO) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/operator-groups/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update group')
      }

      const data = await res.json()
      return data.group as OperatorGroup
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const deleteGroup = useCallback(async (id: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/operator-groups/${id}`, {
        method: 'DELETE',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete group')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    isLoading,
    error,
  }
}
